import DateUtils from '@/shared/utils/DateUtils';
import type ETipoDeDado from '@/shared/enums/ETipoDeDado';
import type { TTypeDinamico } from '@/types/TTypeDinamico';
import type { TFormatoValor } from '@/shared/utils/FormatterUtil';

export type TGrupoMapeamento<Grupo> = {
  nome: Grupo;
  icone: string;
  ocultar?: boolean;
};

export type TFiltroFavoritoStorage = {
  nomeGrupo: string;
  campoClasse: string;
};

export type TMapeamento<Classe, Grupo> = {
  ocultar?: boolean;
  tipo: ETipoDeDado;
  formato?: TFormatoValor;
  campoDisplay: string;
  campoClasse: TTypeDinamico<Classe>;
  grupo: TGrupoMapeamento<Grupo>;
  campoIcone: string;
  valoresPossives?: { texto: string; valor: any }[];
  favoritado?: boolean;
};

export default class CBaseMapeamento<Classe, Grupo> {
  private readonly grupos: TGrupoMapeamento<Grupo>[] = [];
  private readonly mapeamentos: TMapeamento<Classe, Grupo>[] = [];

  constructor() {}

  public add(pObj: TMapeamento<Classe, Grupo>): void {
    // Adiciona o mapeamento
    this.mapeamentos.push(pObj);

    // Verifica se o grupo já existe e adiciona-o se for novo
    const grupoExistente = this.grupos.find((g) => g.nome === pObj.grupo.nome);
    if (!grupoExistente) {
      this.grupos.push(pObj.grupo);
    }
  }

  public possuiMapeamentos(): boolean {
    if (this.mapeamentos.length > 0) return true;
    else return false;
  }

  public normalizarValoresDeUmCampo<Retorno>(pMapeamento: TMapeamento<Classe, Grupo>, pValores: any[]): Retorno[] {
    try {
      const valoresNormalizados = [];

      for (let i = 0; i < pValores.length; i++) {
        const valor = CBaseMapeamento.normalizar(pMapeamento, pValores[i]);
        valoresNormalizados.push(valor);
      }

      return valoresNormalizados;
    } catch (error) {
      throw error;
    }
  }

  public getMapeamentoPorCampoClasse(pCampoClasse: string): TMapeamento<Classe, Grupo> {
    if (!this.mapeamentos) throw `'Mapeamento não encontrado para a classe: ${this.constructor.name}`;

    const mapeamento = this.mapeamentos.find((map) => map.campoClasse === pCampoClasse);

    if (!mapeamento) {
      throw `Mapeamento não encontrado para o campo: ${pCampoClasse}`;
    }

    return mapeamento;
  }

  public getMapeamentoPorCampoDisplay(pCampoDisplay: string): TMapeamento<Classe, Grupo> {
    if (!this.mapeamentos) throw `Mapeamento não encontrado para a classe: ${this.constructor.name}`;

    const mapeamento = this.mapeamentos.find((map) => map.campoDisplay === pCampoDisplay);

    if (!mapeamento) {
      throw `Mapeamento não encontrado para o campo: ${pCampoDisplay}`;
    }

    return mapeamento;
  }

  /**
   * Retorna um array com os campos a serem exibidos
   * (Aqueles com acentuação, espaços, etc)
   */
  public getCamposDisplay(): string[] {
    if (!this.mapeamentos || this.mapeamentos.length === 0) return [];
    return this.mapeamentos.map((registro) => registro.campoDisplay);
  }

  /**
   * Retorna um array com as propriedades da clase
   * (Os nomes reais)
   */
  public obterCamposClasse(): (keyof Classe)[] {
    if (!this.mapeamentos || this.mapeamentos.length === 0) return [] as any;
    return this.mapeamentos.filter((item) => !item.ocultar).map((registro) => registro.campoClasse) as any;
  }

  public obterTipoPorCampoDisplay(pCampoDisplay: string): ETipoDeDado {
    const mapeamento = this.getMapeamentoPorCampoDisplay(pCampoDisplay);
    return mapeamento.tipo;
  }

  public obterCampoDisplayPorCampoClasse(pCampoClasse: string): string {
    const mapeamento = this.getMapeamentoPorCampoClasse(pCampoClasse);
    return mapeamento.campoDisplay;
  }

  public obterIconePorCampoClasse(pCampoClasse: string): string {
    const mapeamento = this.getMapeamentoPorCampoClasse(pCampoClasse);
    return mapeamento.campoIcone;
  }

  public obterGrupos(): TGrupoMapeamento<Grupo>[] {
    return this.grupos.filter((map) => !map.ocultar);
  }

  public obterCamposPorGrupo(pGrupoNome: Grupo): TMapeamento<Classe, Grupo>[] {
    return this.mapeamentos.filter((map) => map.grupo.nome === pGrupoNome && !map.ocultar);
  }

  public obterMapeamentos(pDesconsiderarOcultos: boolean = true): TMapeamento<Classe, Grupo>[] {
    if (pDesconsiderarOcultos) {
      return this.mapeamentos.filter((map) => !map.ocultar);
    }

    return this.mapeamentos;
  }
  
  public obterIcones(): string[] {
    return [
      ...new Set(
        this.mapeamentos.filter((map) => map.campoIcone && map.campoIcone.trim() !== '').map((item) => item.campoIcone),
      ),
    ];
  }

  public alterarVisibilidadeCamposMapeamentoPorCampoClasse<Classe>(pCamposClasse: Array<keyof Classe>) {
    for (const map of this.mapeamentos) {
      if (pCamposClasse.includes(map.campoClasse as any)) {
        map.ocultar = !map.ocultar;
      }
    }
  }

  public alterarVisibilidadePorGrupo<Grupo>(pGrupos: Array<Grupo>) {
    for (const grup of this.grupos) {
      if (pGrupos.includes(grup.nome as any)) {
        grup.ocultar = !grup.ocultar;
      }
    }
  }

  //------------------------------------------------------------
  //        TRATAMENTO DE DADOS
  //------------------------------------------------------------

  /**
   * Realiza o tratamento dos dados recebidos para uso na aplicação
   * @param pMapeamento
   * @param pValor
   */
  static normalizar(pMapeamento: TMapeamento<any, any>, pValor: any) {
    try {
      switch (pMapeamento.tipo) {
        case 'Integer':
          pValor = parseInt(pValor, 10);
          if (Number.isNaN(pValor)) {
            throw new Error(`Valor inválido para o campo ${pMapeamento.campoDisplay}`);
          }
          return pValor;

        case 'Boolean':
          return pValor == 'true';

        default:
          return pValor;
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Realiza o tratamento dos dados para serem devolvidos ao backend
   * @param pMapeamento
   * @param pValor
   */
  static serializar(pMapeamento: TMapeamento<any, any>, pValor: any) {
    try {
      switch (pMapeamento.tipo) {
        case 'Date':
        case 'DateTime':
          return DateUtils.parse(pValor).toString(); //.toJSDate()

        default:
          return pValor;
      }
    } catch (error) {
      throw error;
    }
  }
}
